import styled from "@emotion/styled";
import React from "react";
import { Marker, useMap } from "react-map-gl/maplibre";
import { PublicVisitBroadcastDetails } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { getLocationDisplayLocation } from "../../../utils/addressComponentUtils";
import BaseMap from "../../BaseMap";
import { AppIcon } from "../../Generic/AppIcon";
import { AppIconColor } from "../../Generic/AppIconColors";
import { If } from "../../Generic/If";
import { Slide, SlideProps, SlideStepLabel, SlideTitle } from "./Slide";
import { default as MapMarkerIconSvg } from "/public/assets/map-marker-icon.svg?react";
import QueryRenderer from "../../QueryRenderer";
import { TextShimmer } from "../../Shimmer";
import { DirectionsCarRoundedIcon, DirectionsBusIcon } from "../../../utils/icones";
import { AppButton } from "../../Generic/AppButton";

export const Slide4Address = (props: SlideProps) => {
  const { visit, isBigScreen } = props;
  const addressLocation = visit.publicVisitBroadcastDetails.location.location;
  const address = getAddressFromVisitDetails(visit.publicVisitBroadcastDetails);

  return (
    <Slide>
      <If condition={props.showSteps}>
        <SlideStepLabel {...props} />
      </If>
      <SlideTitle>{translate(STRINGS.ADDRESS)}</SlideTitle>
      <AddressInformation>
        <Address>{address}</Address>
      </AddressInformation>
      <TransportationsContainer>
        {[
          { icon: DirectionsCarRoundedIcon, property: "carTravelTime" as const },
          { icon: DirectionsBusIcon, property: "busTravelTime" as const },
        ].map(({ icon, property }) => (
          <React.Fragment key={property}>
            <Transporation>
              <TransportationIcon as={icon} />
              {props.travelTimesQuery !== null && (
                <QueryRenderer
                  query={props.travelTimesQuery}
                  onLoading={() => <TextShimmer height={19} width={38} />}
                  onSuccess={(data) => <TransportationText>{data[property]}</TransportationText>}
                  onError={() => <TransportationText>?</TransportationText>}
                />
              )}
            </Transporation>
          </React.Fragment>
        ))}
      </TransportationsContainer>
      <MapContainer className={isBigScreen ? "big" : "small"}>
        <BaseMap destination={addressLocation} zoom={15}>
          <MapMarker position={addressLocation} />
        </BaseMap>
      </MapContainer>
      <OpenMapsButton onClick={props.onClickLocation} variant="outline">
        {translate(STRINGS.OPEN_MAPS)}
      </OpenMapsButton>
    </Slide>
  );
};

const getAddressFromVisitDetails = (visitDetails: PublicVisitBroadcastDetails) => {
  if (visitDetails.location.formatedAddressDetails !== undefined) {
    const address = visitDetails.location.formatedAddressDetails;
    return address.fullAddress;
  }

  // For older versions that might not have the new type of address with the full address, show the old address format instead.
  return getLocationDisplayLocation(visitDetails.location);
};

interface MapMarkerProps {
  position: google.maps.LatLngLiteral;
  width?: number;
  height?: number;
  color?: AppIconColor;
}

const MapMarker = (props: MapMarkerProps) => {
  const map = useMap();

  const width = props.width ?? 48;
  const height = props.height ?? 48;

  const handleClickMarker = () =>
    map.current?.flyTo({ zoom: 15, center: props.position, animate: true, duration: 1000 });

  return (
    <Marker
      latitude={props.position.lat}
      longitude={props.position.lng}
      onClick={handleClickMarker}
    >
      <AppIcon icon={<MapMarkerIconSvg />} fill={props.color} style={{ width, height }} />
    </Marker>
  );
};

const Address = styled.span`
  font-weight: 900;
`;

const AddressInformation = styled.div`
  padding: 16px 0;
  font-size: 16px;
  line-height: 120%;
`;

const MapContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;

  &.big {
    width: 100%;
    padding-bottom: 100%;
    position: relative;

    & > div {
      position: absolute;
      width: 100%;
      height: 100%;
    }
  }

  &.small {
    height: 200px;

    & > div {
      height: 100%;
    }
  }
`;
const TransportationsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 12px;
`;

const Transporation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TransportationIcon = styled.svg`
  font-size: 24px;
  color: var(--tw-slate-400);
`;

const TransportationText = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const OpenMapsButton = styled(AppButton)`
  margin-top: 12px;
  width: 100%;
`;
